/**
 * Every box of /channels/:platform as one labelled table, the single source of
 * the chat (channelChatContext) and the file (channelCsv), so they cannot drift
 * (R22). As D1's overviewTables: raw numbers (float noise removed), NULL stays
 * null, ratios and deltas in percent, money headers name the currency, `kinds`
 * say how the chat shows a cell. A tile the page words ("ไม่ได้รายงาน", "v1 ไม่แสดง")
 * carries that word (R35), and so does a Reaction · Comment · Share the platform
 * did not send (rules-04 D4); every breakdown tab is written whatever tab is open,
 * closed by its "รวม" line when it has rows (an ad tab with no ad row is written
 * "empty", under its coverage lines). Pure.
 *
 * @module services/channels/channelTables
 */
import type { TFunction } from 'i18next';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { creativeNameOf } from '@/services/creatives/creativeText';
import type { CsvCell } from '@/services/csv';
import { fmtNamingCoverage } from '@/services/looker/lookerCoverage';
import {
  bare, each, grainHeader, METRIC_KINDS, metricCells, metricColumns, num, pct, scorecardTable, shell, trendTable, unit,
  valueColumns, type Built,
} from '@/services/overview/boxTableParts';
import { fmtPct, objectiveText } from '@/services/overview/overviewFormat';
import type { BoxTable, CellKind } from '@/services/overview/overviewTables';
import type { BoxState, CurrencyChoice, ObjectiveFilter, OverviewWindows } from '@/services/overview/overviewTypes';
import {
  CHANNEL_LABEL, campaignStateText, familyLabel, fmtShortDate, formatLabel, platformNotes, tileReason, tileTitle, typeLabel,
} from './channelFormat';
import {
  UNCLASSIFIED_TYPE, hasEngagementColumns,
  type AdGrainCoverage, type AdGrainTable, type BreakdownMetrics, type BreakdownTable, type BreakdownView,
  type CampaignBreakdownRow, type ChannelEmpty, type ChannelPlatform, type ChannelView, type CreativeBreakdownRow,
  type FormatBreakdownRow, type Ga4View, type PlatformTile, type TargetingBreakdown, type TargetingBreakdownRow,
} from './channelTypes';

/** An ad channel's boxes in page order (spec §3.2): 5a Campaign, 5b Ad format, 5c Targeting, 5d its segments, 5e Creative. */
export const CHANNEL_LAYOUT = ['2', '3', '4', '5a', '5b', '5c', '5d', '5e'] as const;
/** The GA4 tab: its tiles, then its chart (spec §3.5). */
export const GA4_LAYOUT = ['ga4', 'ga4-trend'] as const;
/** The tables whose last row, when they have rows, is their tab's "รวม" line; the chat tells it as `totals`. */
export const TOTAL_ROW_IDS: ReadonlySet<string> = new Set(['5a', '5b', '5c', '5e']);

type TableId = (typeof CHANNEL_LAYOUT)[number] | (typeof GA4_LAYOUT)[number];
type Titles = Readonly<Record<TableId, string>>;

/** Everything the chat and the file say about the page, assembled once by the page. */
export interface ChannelPageInput {
  title: string;
  platform: ChannelPlatform;
  channelLabel: string;
  allWorkspaces: boolean;
  workspaceName: string | null;
  windows: OverviewWindows;
  latestDataDate: string | null;
  money: CurrencyChoice | null;
  objectiveLabel: string;
  /** The filters in force, worded as the page shows them: the chat's `filters` (the period travels as `period`). */
  filters: Array<{ title: string; value: string }>;
  /** The applied Campaign type filter, worded (R11); [] = no type filter. */
  typeLabels: string[];
  /** The applied Campaign filter by name, with the workspace in all workspaces (R25); [] = no campaign filter. */
  campaignLabels: string[];
  /** A as applied; null where the page draws no Active switch (GA4, E, box 5 not drawn). */
  activeOnly: boolean | null;
  /** The non-Active campaigns A hides (R16); null = unknown (facts not read) or not applicable. */
  hidden: number | null;
  tables: BoxTable[];
  /** The ad tabs' coverage (R17); null on GA4, under E, or before the ads are drawn. */
  adGrain: AdGrainCoverage | null;
  freshness: ChannelFreshnessState;
}

/** cur: the page currency, named in every money header. */
interface Ctx { platform: ChannelPlatform; windows: OverviewWindows; cur: string | null; t: TFunction }

/** The page's own titles (the grid uses the same keys); 5d is the segment table under Targeting. */
const titlesOf = (c: Ctx): Titles => {
  const channel = CHANNEL_LABEL[c.platform];
  const box5 = (tab: string): string => `${c.t('channels.box.breakdown')} · ${tab}`;
  return {
    '2': c.t('channels.box.scorecards', { channel }),
    '3': c.t('channels.box.tiles', { channel }),
    '4': c.t('channels.box.trend'),
    '5a': box5(c.t('channels.breakdown.tabs.campaign')),
    '5b': box5(c.t('channels.breakdown.tabs.format')),
    '5c': box5(c.t('channels.breakdown.tabs.targeting')),
    '5d': c.t('channels.csv.segments'),
    '5e': box5(c.t('channels.breakdown.tabs.creative')),
    ga4: c.t('channels.box.ga4'),
    'ga4-trend': c.t('channels.ga4.chartTitle'),
  };
};

/** A tile's unit in its label: the currency, %, or x (ROAS is a multiple); a static tile has none. */
const tileUnit = (tile: PlatformTile, cur: string | null): string | null => {
  if (tile.isStatic) return null;
  if (tile.format === 'money' || tile.format === 'money4') return cur;
  if (tile.format === 'pct') return '%';
  return tile.format === 'roas' ? 'x' : null;
};

/** A tile's figures are its format's kind; a static tile's (it has none) are count, so the file writes "—" (rules-04 D4). */
const tileKind = (tile: PlatformTile): CellKind => {
  if (tile.format === 'money') return 'money';
  if (tile.format === 'money4') return 'money4';
  return tile.format === 'pct' ? 'pct' : 'count';
};

const tileNum = (tile: PlatformTile, n: number | null): CsvCell => (tile.format === 'pct' ? pct(n) : num(n));
/** The value cell's kind: a figure, or the word (or nothing to count) the page shows in its place; the previous period keeps the tile's own kind. */
const valueKind = (tile: PlatformTile): CellKind => (tile.state === 'value' ? tileKind(tile) : 'word');

/** R35: "ไม่ได้รายงาน" (static, or every row NULL; D1's word, rules-04 D4) and "v1 ไม่แสดง" are written as the page words them; any other NULL stays null. */
const tileValue = (tile: PlatformTile, t: TFunction): CsvCell => {
  if (tile.state === 'notShown') return t('channels.notShownV1');
  if (tile.state === 'notReported') return t('overview.notReported');
  return tile.state === 'value' ? tileNum(tile, tile.value) : null;
};

/**
 * Box 3 and the GA4 tiles. While the cut is in force (FR-X-01) the conversion
 * family carries the chip in its own column, and the table carries
 * resultThrough / resultPrevious, as D1's Result boxes do. GA4 has no cut (R15).
 * While a ratio tile is faded, "n<500" has the last column, as in box 2 (rules-04 D1).
 * CPA, CPL and ROAS name the objective they read before their unit: "CPL · Conversion (THB)" under "all"
 * (rules-04 E4), "CPA · Awareness (THB)" with Awareness selected (rules-04 H1). A tile that reads "—" because that
 * objective has no row says why in a column of its own, as n<500 does, headed by the line itself: one objective is
 * named per page, so one line (rules-04 H2). The value cell keeps its "—".
 */
const tilesTable = (id: TableId, title: string, state: BoxState<PlatformTile[]>, c: Ctx): BoxTable =>
  shell(id, title, state, (tiles) => {
    const cut = c.windows.cutApplied && tiles.some((x) => x.cut);
    const low = tiles.some((x) => x.lowSample);
    const chip = c.t('overview.cutChip');
    const lowSample = c.t('overview.lowSample');
    const reasons = tiles.map((x) => tileReason(x, c.t));
    const reason = reasons.find((r) => r !== null) ?? null;
    const w = c.windows;
    return {
      columns: [...valueColumns(c.t), ...(cut ? [chip] : []), ...(reason ? [reason] : []), ...(low ? [lowSample] : [])],
      rows: tiles.map((x, i) => [
        unit(tileTitle(x, c.t), tileUnit(x, c.cur)), tileValue(x, c.t),
        x.isStatic ? null : tileNum(x, x.previous), pct(x.delta), ...(cut ? [x.cut ? chip : null] : []),
        ...(reason ? [reasons[i]] : []), ...(low ? [x.lowSample ? lowSample : null] : []),
      ]),
      kinds: tiles.map((x): CellKind[] => [
        'text', valueKind(x), tileKind(x), 'pct', ...(cut ? ['text' as const] : []), ...(reason ? ['text' as const] : []),
        ...(low ? ['text' as const] : []),
      ]),
      notes: platformNotes(c.platform, c.t),
      ...(cut ? { resultThrough: w.through, ...(w.cutPrevious ? { resultPrevious: w.cutPrevious } : {}) } : {}),
    };
  });

// ---------------------------------------------------------------- box 5

const ENGAGEMENT_KINDS: CellKind[] = ['count', 'count', 'count'];

/** The six metrics' columns (D1's), Facebook's and TikTok's Reaction · Comment · Share, then n<500: the page's columns. */
const breakdownColumns = (c: Ctx): string[] => [
  ...metricColumns(c.t, c.cur),
  ...(hasEngagementColumns(c.platform) ? [c.t('channels.col.reaction'), c.t('channels.col.comment'), c.t('channels.col.share')] : []),
  c.t('overview.lowSample'),
];

/** A Reaction · Comment · Share the platform did not send is the page's "ไม่ได้รายงาน" (rules-04 D4), never 0. */
const engagementCell = (n: number | null, c: Ctx): CsvCell => (n === null ? c.t('overview.notReported') : num(n));

const breakdownCells = (m: BreakdownMetrics, c: Ctx): CsvCell[] => [
  ...metricCells(m),
  ...(hasEngagementColumns(c.platform) ? [engagementCell(m.reactions, c), engagementCell(m.comments, c), engagementCell(m.shares, c)] : []),
  m.lowSample ? c.t('overview.lowSample') : null,
];

/** TikTok's Reaction column counts likes: the note under every table that has the column, as on the page (D3 spec §5). */
const engagementNotes = (c: Ctx): string[] => (c.platform === 'tiktok' ? [c.t('channels.breakdown.tiktokReaction')] : []);

/** A tab's own leading columns (all text) and their cells for one row. */
interface Lead<R> { columns: string[]; cells: (row: R) => CsvCell[] }

/**
 * Every row, then "รวม" over exactly those rows (R16), blank under the lead columns that do not add up. The tab's own
 * notes come first, then TikTok's.
 */
const tabParts = <R extends BreakdownMetrics>(table: BreakdownTable<R>, lead: Lead<R>, c: Ctx, notes: string[] = []): Built => {
  const blanks = lead.columns.slice(1).map((): CsvCell => null);
  const rows = [
    ...table.rows.map((r) => [...lead.cells(r), ...breakdownCells(r, c)]),
    [c.t('channels.breakdown.total'), ...blanks, ...breakdownCells(table.total, c)],
  ];
  const engagement = hasEngagementColumns(c.platform) ? ENGAGEMENT_KINDS : [];
  const kinds: CellKind[] = [...lead.columns.map((): CellKind => 'text'), ...METRIC_KINDS, ...engagement, 'text'];
  return {
    columns: [...lead.columns, ...breakdownColumns(c)], rows, kinds: each(rows, kinds), notes: [...notes, ...engagementNotes(c)],
  };
};

/**
 * The campaign name, its workspace (all workspaces only), type, the "Unclassified" mark of a name outside the
 * convention (D1 box 13's column, rules-04 E1), state and native id.
 */
const campaignLead = (rows: readonly CampaignBreakdownRow[], c: Ctx): Lead<CampaignBreakdownRow> => {
  const withWs = rows.some((r) => r.workspaceName !== null);
  const unclassified = c.t('looker.coverage.unclassified');
  return {
    columns: [
      c.t('channels.col.campaign'), ...(withWs ? [c.t('looker.admin.colWorkspace')] : []),
      c.t('channels.filters.type'), unclassified, c.t('channels.col.status'), c.t('channels.col.nativeId'),
    ],
    cells: (r) => [
      r.name, ...(withWs ? [r.workspaceName] : []), typeLabel(r.type, c.t), r.conforms ? null : unclassified,
      campaignStateText(r.state, c.t), r.nativeId,
    ],
  };
};

const formatLead = (c: Ctx): Lead<FormatBreakdownRow> =>
  ({ columns: [c.t('channels.col.format')], cells: (r) => [formatLabel(r.format, c.t)] });
const targetingLead = (c: Ctx): Lead<TargetingBreakdownRow> =>
  ({ columns: [c.t('channels.col.adSet'), c.t('channels.csv.segments')], cells: (r) => [r.name, familyLabel(r.family, c.t)] });
/** The ad name as the page words it (an unnamed ad reads as on D3), then its format: the line the page draws under the name (D3 §5, R3). */
const creativeLead = (c: Ctx): Lead<CreativeBreakdownRow> => ({
  columns: [c.t('channels.col.creative'), c.t('channels.col.format')],
  cells: (r) => [creativeNameOf(r, c.t), formatLabel(r.format, c.t)],
});

/** "Naming coverage X%" with its chip, as the breakdown header (floored, rules-04 E2); "—" and no chip when no shown campaign spent. */
const namingNotes = (b: BreakdownView, t: TFunction): string[] => {
  if (b.namingCoverage === null) return [];
  const { share } = b.namingCoverage;
  const line = t('channels.breakdown.namingCoverage', { pct: fmtNamingCoverage(share) });
  if (share === null) return [line];
  return [`${line} · ${share === 1 ? t('channels.breakdown.namingComplete') : t('channels.breakdown.namingIncomplete')}`];
};

/** Ad grain (R17): the share of the spend the ad tabs hold, and how much of it has a format; unknown is "—". */
const adGrainNotes = (cov: AdGrainCoverage, t: TFunction): string[] => [
  t('channels.breakdown.adShare', { pct: fmtPct(cov.adShare, 1) }),
  t('channels.breakdown.formatShare', { pct: fmtPct(cov.formatShare, 1) }),
];

/** An ad tab with its coverage lines. With no ad row (P10: the ad sets spent, no ad exists) they stand over "empty", as the page draws them, not over a "รวม" of nulls. */
const adTab = <R extends BreakdownMetrics>(
  id: string, title: string, state: BoxState<AdGrainTable<R>>, lead: Lead<R>, c: Ctx,
): BoxTable => {
  if (state.status === 'ready' && state.data.rows.length === 0) return bare(id, title, 'empty', adGrainNotes(state.data.coverage, c.t));
  return shell(id, title, state, (tab) => tabParts(tab, lead, c, adGrainNotes(tab.coverage, c.t)));
};

/** The segment charts as a table; Google says why it has none (S6.1); a family under 500 impressions has no CTR / CPC, and only it earns the note, as on the page (R22). */
const segmentTable = (state: BoxState<TargetingBreakdown>, title: string, c: Ctx): BoxTable => {
  if (state.status !== 'ready') return bare('5d', title, state.status);
  if (c.platform === 'google') return bare('5d', title, 'empty', [c.t('channels.breakdown.googleNoSegments')]);
  const points = state.data.segments ?? [];
  if (points.length === 0) return bare('5d', title, 'empty');
  return {
    id: '5d', title, status: 'ok',
    columns: [
      title, unit(c.t('channels.breakdown.segmentShare'), '%'), unit(c.t('channels.breakdown.segmentCtr'), '%'),
      unit(c.t('channels.breakdown.segmentCpc'), c.cur), c.t('overview.lowSample'),
    ],
    rows: points.map((s) => [familyLabel(s.family, c.t), pct(s.share), pct(s.ctr), num(s.cpc), s.lowSample ? c.t('overview.lowSample') : null]),
    kinds: each(points, ['text', 'pct', 'pct', 'money', 'text']),
    notes: points.some((s) => s.lowSample) ? [c.t('channels.breakdown.segmentLowSample')] : [],
  };
};

/** All four tabs and the segments (R22); the whole box fails or loads with the entities (R8), each tab with its own read. */
const breakdownTables = (state: BoxState<BreakdownView>, titles: Titles, c: Ctx): BoxTable[] => {
  if (state.status !== 'ready') {
    const { status } = state;
    return (['5a', '5b', '5c', '5d', '5e'] as const).map((id) => bare(id, titles[id], status));
  }
  const b = state.data;
  return [
    shell('5a', titles['5a'], b.campaign, (tab) => tabParts(tab, campaignLead(tab.rows, c), c, namingNotes(b, c.t))),
    adTab('5b', titles['5b'], b.format, formatLead(c), c),
    shell('5c', titles['5c'], b.targeting, (tab) => tabParts(tab, targetingLead(c), c)),
    segmentTable(b.targeting, titles['5d'], c),
    adTab('5e', titles['5e'], b.creative, creativeLead(c), c),
  ];
};

/**
 * E (R7) in place of every box: each says the card's sentence and nothing else, as on D1 and D4, and under "no spend"
 * the first one also carries the last-run date, when known (rules-04 H review F2, I2).
 */
const emptyTables = (e: ChannelEmpty, ids: readonly TableId[], titles: Titles, c: Ctx): BoxTable[] => {
  const channel = CHANNEL_LABEL[c.platform];
  const sentence = c.t(e.kind === 'notConnected' ? 'channels.empty.notConnected' : 'channels.empty.noSpend', { channel });
  const lastRun = e.kind === 'noSpend' && e.lastSpendDate
    ? [c.t('channels.empty.lastRun', { date: fmtShortDate(e.lastSpendDate, c.t) })]
    : [];
  return ids.map((id, i) => ({
    ...bare(id, titles[id], 'empty', i === 0 ? [sentence, ...lastRun] : [sentence]), notesOnly: true,
  }));
};

/** buildChannelView always sets ga4 on the GA4 tab; were it missing, both tables say failed, as the grid does. */
const NO_GA4: BoxState<Ga4View> = { status: 'failed' };

/** The GA4 tiles, then the chart: sessions and web conversions are site-wide, the ad spend is in the page currency (R15). */
const ga4Tables = (state: BoxState<Ga4View>, titles: Titles, c: Ctx): BoxTable[] => {
  const tiles: BoxState<PlatformTile[]> = state.status === 'ready' ? { status: 'ready', data: state.data.tiles } : state;
  return [
    tilesTable('ga4', titles.ga4, tiles, c),
    shell('ga4-trend', titles['ga4-trend'], state, (g) => ({
      columns: [
        grainHeader(g.grain, c.t), c.t('channels.tile.sessions'), c.t('channels.tile.webConversions'),
        unit(c.t('channels.ga4.adSpend'), c.cur),
      ],
      rows: g.points.map((p) => [p.key, num(p.sessions), num(p.webConversions), num(p.adSpend)]),
      kinds: each(g.points, ['text', 'count', 'count', 'money']),
    })),
  ];
};

/** The page's boxes in layout order: E in place of all of them, else boxes 2–5 or the GA4 panel. */
export const channelTablesOf = (input: {
  view: ChannelView; platform: ChannelPlatform; windows: OverviewWindows; t: TFunction;
}): BoxTable[] => {
  const { view, platform, windows, t } = input;
  const c: Ctx = { platform, windows, cur: view.money?.currency ?? null, t };
  const titles = titlesOf(c);
  const layout = platform === 'analytics' ? GA4_LAYOUT : CHANNEL_LAYOUT;
  if (view.empty) return emptyTables(view.empty, layout, titles, c);
  if (platform === 'analytics') return ga4Tables(view.ga4 ?? NO_GA4, titles, c);
  return [
    scorecardTable(view.scorecards, titles['2'], c.cur, c.t),
    tilesTable('3', titles['3'], view.tiles, c),
    trendTable(view.trend, titles['4'], c.cur, c.t),
    ...breakdownTables(view.breakdown, titles, c),
  ];
};

// ---------------------------------------------------------------- the page input

/** What the page knows when it assembles its input; view and windows are the ones the grid draws. */
export interface ChannelPageArgs {
  view: ChannelView; platform: ChannelPlatform; windows: OverviewWindows; objective: ObjectiveFilter;
  allWorkspaces: boolean; workspaceName: string | null; latestDataDate: string | null;
  freshness: ChannelFreshnessState; t: TFunction;
}

type RawFilters = Pick<ChannelPageInput, 'typeLabels' | 'campaignLabels' | 'activeOnly' | 'hidden'>;

/** The filters as applied (R11), as values; GA4 takes none of them (R15), A exists only where box 5 is drawn. */
const rawFiltersOf = ({ view, platform, t }: ChannelPageArgs): RawFilters => {
  if (platform === 'analytics') return { typeLabels: [], campaignLabels: [], activeOnly: null, hidden: null };
  const options = view.options.status === 'ready' ? view.options.data.campaigns : [];
  const campaignLabels = view.applied.campaigns.map((id) => {
    const o = options.find((x) => x.value === id);
    if (!o) return id;
    return o.workspaceName ? `${o.name} (${o.workspaceName})` : o.name;
  });
  const typeLabels = view.applied.types.map((v) => typeLabel(v === UNCLASSIFIED_TYPE ? null : v, t));
  const b = view.breakdown;
  if (view.empty || b.status !== 'ready') return { typeLabels, campaignLabels, activeOnly: null, hidden: null };
  return { typeLabels, campaignLabels, activeOnly: b.data.activeOnly, hidden: b.data.hidden };
};

/** The chat's `filters`; A wherever the page draws it: on as its switch, the count only when N > 0 (R37), off as the file. */
const filtersOf = (platform: ChannelPlatform, objectiveLabel: string, raw: RawFilters, t: TFunction): ChannelPageInput['filters'] => {
  const objective = { title: t('overview.filters.objective'), value: objectiveLabel };
  if (platform === 'analytics') return [objective];
  const listed = (labels: string[]): string => (labels.length > 0 ? labels.join(', ') : t('channels.filters.all'));
  const label = t('channels.breakdown.activeOnly');
  const on = raw.hidden ? `${label} ${t('channels.breakdown.hidden', { n: raw.hidden })}` : label;
  const active = raw.activeOnly ? on : t('channels.csv.activeOff');
  return [
    objective,
    { title: t('channels.filters.type'), value: listed(raw.typeLabels) },
    { title: t('channels.filters.campaign'), value: listed(raw.campaignLabels) },
    ...(raw.activeOnly === null ? [] : [{ title: t('channels.box.breakdown'), value: active }]),
  ];
};

/** The ad tabs share one coverage (R17); none under E or before they are drawn. */
const adGrainOf = (view: ChannelView): AdGrainCoverage | null => {
  if (view.empty || view.breakdown.status !== 'ready') return null;
  const { format, creative } = view.breakdown.data;
  if (format.status === 'ready') return format.data.coverage;
  return creative.status === 'ready' ? creative.data.coverage : null;
};

export const channelPageInputOf = (a: ChannelPageArgs): ChannelPageInput => {
  const objectiveLabel = a.objective === 'all' ? a.t('looker.objectives.all') : objectiveText(a.objective, a.t);
  const raw = rawFiltersOf(a);
  return {
    title: a.t('channels.title'), platform: a.platform, channelLabel: CHANNEL_LABEL[a.platform],
    allWorkspaces: a.allWorkspaces, workspaceName: a.allWorkspaces ? null : a.workspaceName,
    windows: a.windows, latestDataDate: a.latestDataDate, money: a.view.money, objectiveLabel,
    filters: filtersOf(a.platform, objectiveLabel, raw, a.t), ...raw,
    tables: channelTablesOf({ view: a.view, platform: a.platform, windows: a.windows, t: a.t }),
    adGrain: adGrainOf(a.view), freshness: a.freshness,
  };
};
